import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { FacilityCard, useFacilities } from '../../features/facilities';

const FacilitiesPage: React.FC = () => {
  const navigate = useNavigate();
  
  const { 
    facilities, 
    loading, 
    error, 
    deleteFacility
  } = useFacilities();
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [facilityToDelete, setFacilityToDelete] = useState<string | null>(null);

  const handleEdit = (id: string) => {
    navigate(`/facilities/edit/${id}`);
  };

  const handleView = (id: string) => {
    navigate(`/facilities/view/${id}`);
  }

  const handleDeleteClick = (id: string) => {
    setFacilityToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (facilityToDelete) {
      const { success } = await deleteFacility(facilityToDelete);
      if (success) {
        setDeleteDialogOpen(false);
        setFacilityToDelete(null);
      }
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setFacilityToDelete(null);
  };

  if (loading && facilities.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" component="h1">Facilities</Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => navigate('/facilities/new')}
        >
          Add Facility
        </Button>
      </Box>

      {facilities.length > 0 ? (
        <Grid container spacing={3}>
          {facilities.map((facility) => (
            <Grid item xs={12} sm={6} md={4} key={facility.id}>
              <FacilityCard 
                facility={facility}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8, 
          border: '1px dashed', 
          borderColor: 'divider',
          borderRadius: 1
        }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No facilities found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get started by adding your first facility
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => navigate('/facilities/new')}
          >
            Add Facility
          </Button>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Facility</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this facility? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error"
            variant="contained"
            startIcon={<Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FacilitiesPage;
